/* eslint-disable no-param-reassign */

import Immutable from 'immutable';

import { pageContext, pageRulesStateToProps, get } from 'ctm-util-functions';

const DEBUG = false;

/**
 * Log a debug message if DEBUG is true
 * @param {*} msg - The message to log
 */
const debugMessage = (...msg) => {
    if (DEBUG) {
        // eslint-disable-next-line no-console
        console.info(...msg);
    }
};

/**
 * Temporary function until Immutable is out of RC and I can use Immutable.isCollection()
 * @param {*} obj - The object to test
 * @returns {Boolean} - True if obj is an Immutable Map or List, false otherwise
 */
export const isImmutableCollection = (obj) => {
    return ((obj instanceof Immutable.Map) || obj instanceof Immutable.List);
};

/**
 * Returns true if value is null, undefined, an empty string or an empty collection
 * @param {*} value - The value to check
 * @returns {Boolean} - true if the value is 'empty' false otherwise
 */
export const isEmpty = (value) => {
    return [null, undefined, ''].includes(value) || (isImmutableCollection(value) && !value.size);
};

class FormPayloadModel {

    constructor(state, visibilityRules = null, validationRules = null) {
        const props = pageContext.getProps();
        this.formSchemaModel = props.models.formSchemaModel;
        this.validationModel = props.models.validationModel;
        this.pageSchemaModel = props.models.pageSchemaModel;
        this.state = state;

        this.allPageNames = this.pageSchemaModel.getAllJourneyPageNames();
        if (visibilityRules && validationRules) {
            // THIS IS ONLY TO MAKE THINGS EASIER DURING UNIT TESTS
            this.visibilityRules = visibilityRules;
            this.validationRules = validationRules;
        } else {
            this.validationRules = {};
            this.visibilityRules = {};
            for (const name of this.allPageNames) {
                Object.assign(this.validationRules, pageRulesStateToProps(name, 'validation')(this.state).rules);
                Object.assign(this.visibilityRules, pageRulesStateToProps(name, 'visibility')(this.state).rules);
            }
        }

        this.valueMap = { Y: true, N: false };
        this.ignoredStateBranches = ['helpers'];
    }

    /**
     * Construct a new, correct visibility Map for all form elements on all pages.
     * The visibility state is always correct for the current page, but sometimes things can be set to hidden on unmount etc
     * This method ensures that we have the correct visibility state, driven directly off the rules to ensure correct validation.
     * @todo: Sort out consistency for visibility state interactions so that this isn't required
     * @param {Immutable.Map} visibility - The current visibility state for elements and fieldSets
     * @param {Object} visibilityRules - All visibility rules for all form elements on all pages
     * @returns {Immutable.Map} - A new visibility map with the results of the visibility rules appled
     */
    setVisibility = (visibility, visibilityRules) => {
        for (const [elementID, ...rest] of visibility) {
            const elementVisibilityRule = visibilityRules[elementID];
            if (elementVisibilityRule && elementVisibilityRule.visibility.result) {
                // element has it's own rule and is visible
                visibility = visibility.set(elementID, true);
            } else if (elementVisibilityRule && !elementVisibilityRule.visibility.result) {
                // element has it's own rule and is invisible
                visibility = visibility.set(elementID, false);
            } else {
                // no element visibility rule, check fieldSet
                const fieldSet = this.formSchemaModel.getResolvedFieldSetForElement(elementID) || this.formSchemaModel.getFieldSet('vertical.quote', elementID);
                if (fieldSet) {
                    // element is in a fieldSet
                    const fieldSetVisibilityRule = visibilityRules[fieldSet.identifier];
                    if (fieldSetVisibilityRule && fieldSetVisibilityRule.visibility.result) {
                        // fieldSet has a visibility rule and is visible
                        visibility = visibility.set(elementID, true);
                        visibility = visibility.set(fieldSet.identifier, true);
                    } else if (fieldSetVisibilityRule && !fieldSetVisibilityRule.visibility.result) {
                        // fieldSet has a visibilityRule and is invisible
                        visibility = visibility.set(elementID, false);
                        visibility = visibility.set(fieldSet.identifier, false);
                    } else {
                        // fieldset has no visibility rule and is always visible
                        visibility = visibility.set(elementID, true);
                        visibility = visibility.set(fieldSet.identifier, true);
                    }
                } else {
                    // element isn't in a fieldset and has no visibility rule and is always visible
                    visibility = visibility.set(elementID, true);
                }
            }
        }
        return visibility;
    };

    /**
     *
     * @param {Array} allElements - The list of elements to check for validity
     * @param {Object} rules - The corresponding validation rules for the elements specified by allElementIDs
     * @param {Immutable.Map} visibility - The corresponding visibility state for the elements specified by allElementIDs
     * @returns {Array} - The list of elementIDs from allElementIDs that have passed their validation rules
     */
    getValidElements = (allElements, rules, visibility) => {
        const validElementIDs = [];
        for (const element of allElements) {
            const { alwaysPost = false } = element;
            const name = element.payloadIdentifier || element.backendIdentifier || element.identifier;
            // Ignore anything that we don't need, eg 'helpers'
            if (this.ignoredStateBranches.includes(name.split('.')[0])) {
                continue;
            }

            const isVisible = this.validationModel.isElementVisible(visibility, element.identifier);
            const rule = this.validationModel.getElementValidationRule(element.identifier, rules, visibility, {});
            if ((rule && rule.result && isVisible) || alwaysPost) {
                // elements without validation rules are considered 'valid' - currently this is only the opt-ins on energy
                validElementIDs.push(name);
                debugMessage(name, `FORM PAYLOAD: element: ${name} is visible and valid`, rule, isVisible);
            } else if (!rule || !isVisible) {
                debugMessage(name, `FORM PAYLOAD: element: ${name} isn't visible and won't be added to the payload`, rule, isVisible);
            } else if (rule && !rule.result) {
                debugMessage(name, `FORM PAYLOAD: element: ${name} isn't valid and won't be added to the payload`, rule, isVisible);
            }
        }
        return validElementIDs;
    };

    /**
     * Sometimes state values are collections that may contain null / empty strings etc which the backend will reject
     * as a bad request - eg propertyAddress.
     * This will recursively iterate and remove nulls, undefined, empty strings and empty collections
     * @param {Immutable.Map|Immutable.List} collection - The collection to sanitize
     * @returns {Immutable.Map|Immutable.List} - The collection with all empty values removed
     */
    sanitizeCollection = (collection) => {
        for (const key of collection.keys()) {
            const value = collection.get(key);
            if (isEmpty(value)) {
                // eslint-disable-next-line no-param-reassign
                collection = collection.delete(key);
            }
            if (isImmutableCollection(value)) {
                const subCollection = this.sanitizeCollection(value);
                if (subCollection.size) {
                    // eslint-disable-next-line no-param-reassign
                    collection = collection.set(key, subCollection);
                } else {
                    // eslint-disable-next-line no-param-reassign
                    collection = collection.delete(key);
                }
            }
        }
        return collection;
    };

    /**
     * Constructs the form payload to send to the backend. The payload is constructed using values from the full
     * quote form state along with the associated visibility and validation rules. This ensures that only the
     * minimum required values are sent to the backend to prevent polluting the DB and to assist with payload
     * validation on the backend.
     * @param {String|Null} statePathPrefix Pass in an optional prefix so that the returned payload has e.g. `some.path.form.data`, instead of just `data`
     * @returns {Immutable.Map} - The form payload only containing the values that need to be sent to the backend
     */
    getFormPayload = (statePathPrefix) => {
        const formState = quoteFormSelector(this.state);
        let visibility = visibilitySelector(this.state);

        // construct the correct visibility state using the visibility rules
        visibility = this.setVisibility(visibility, this.visibilityRules);

        // construct an array of all the form elements on all pages
        const allElements = this.allPageNames
            .reduce((acc, val) => acc.concat(this.formSchemaModel.getPageElements(val)), []);

        // get the element IDs for all valid form elements in the journey
        const validElements = this.getValidElements(allElements, this.validationRules, visibility);

        const extraFormStateKeys = Object.keys(this.formSchemaModel.properties['vertical.quote'].extraFormStateKeys);
        let payload = Immutable.Map();

        for (const elementID of validElements.concat(extraFormStateKeys)) {
            // iterate over the valid elements and the extraFormStateKeys - extraFormStateKeys get set after successful
            // validation of other elements and don't have any corresponding elements or validation or visibility rules
            const idParts = elementID.split('.');
            const value = formState.getIn(idParts);
            if (value === null || value === undefined) {
                // This should only be possible for elementIDs from extraFormStateKeys and MFA
                continue;
            }

            const element = allElements.find((el) => el.identifier === elementID);
            let mappedValue = value;

            // BE now validates the payload with json schema so for user input such as percentage and currency,
            // we need to send as number. However, it is currently only the case in simples
            // so we need a flag to save number explicitly
            // We use get() here as allElements also contains the 'extraFormStateKeys' which don't have corresponding elements
            if (get(element, 'payloadAsNumber', false)) {
                const numberValue = Number.parseFloat(value);
                mappedValue = Number.isNaN(numberValue) ? value : numberValue;
            }

            // Remap the value if it's in the valueMap and set it on the new payload object
            // We map yellow medicare card colour to 'Y', so we have to exclude this from value mapping, otherwise the colour comes through as true
            if (this.valueMap[value] !== undefined && !get(element, 'excludeFromPayloadMapping')) {
                mappedValue = this.valueMap[value];
            } else {
                // Some fields have a valueMap defined in their schema - mainly for multiple radios that need different values
                // but some of them map to a single value - eg concession card type in E2E energy
                const elementValueMap = get(element, 'field.valueMap', {});
                if (elementValueMap[value] !== undefined) {
                    mappedValue = elementValueMap[value];
                }
            }

            if (isImmutableCollection(mappedValue)) {
                mappedValue = this.sanitizeCollection(mappedValue);
                // Put empty array in state for anonymous prefill so that regular_driver_claims page will
                // display and update state correctly with no claims.
                if (!mappedValue.size && elementID !== 'coverDetail.driver.claimsDetails') {
                    continue;
                }
            }
            const payloadIdParts = statePathPrefix ? `${statePathPrefix}.${elementID}`.split('.') : idParts;
            payload = payload.setIn(payloadIdParts, mappedValue);
        }
        return payload;
    };

}

export { FormPayloadModel };

/**
 * Selector to get the correct form payload for sending to the backend
 * NOTE: THIS IS NOT THE COMPLETE FORM STATE! ONLY USE THIS FOR API CALLS TO THE BACKEND. IF YOU NEED THE FULL FORM STATE
 * USE THE `quoteFormSelector()` FROM base.js.
 * @param {Immutable.Map} state - The full application state
 * @param {String|Null} statePathPrefix If you need the payload to have a prefix e.g. vertical.quote.form instead of just returning the contents of `form` (used for snapshots)
 * @returns {Immutable.Map} - The FormPayload state.
 */
export const formPayloadSelector = (state, statePathPrefix = null) => {
    return new FormPayloadModel(state).getFormPayload(statePathPrefix);
};

/**
 * Selector to get the visibility state
 * @param {Immutable.Map} state - The full application state
 * @param {String} stateKey - the parent state node for the form, e.g. vertical.quote, vertical.callMeBack
 * @returns {Immutable.Map} - The visibility state
 */
export const visibilitySelector = (state, stateKey = 'vertical.quote') => {
    return state.getIn([...stateKey.split('.'), 'visibility']);
};

/**
 * Selector to get the form state
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The form state
 */
export const quoteFormSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form']);
};

/**
 * Selector to get the quote state
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The quote state
 */
export const quoteSelector = (state) => {
    return state.getIn(['vertical', 'quote']);
};

/**
 * Selector to get the vertical state
 * @param {Immutable.Map} state - The full application state
 * @returns {Immutable.Map} - The quote state
 */
export const verticalSelector = (state) => {
    return state.get('vertical');
};

/**
 * Selector to get the vehicle state
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The vehicle state
 */
export const vehicleStateSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'vehicle', 'state']);
};

/**
 * Selector to get the address state
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The address state
 */
export const addressStateSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'overnightParking', 'address', 'state']);
};

/**
 * Selector to get the rego
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The rego
 */
export const coverDetailVehicleRegoSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'vehicle', 'rego']);
};

/**
 * Selector to get the rego label
 * @param {Immutable.Map} state - The full application state
 * @returns {String} - The rego label
 */
export const helpersRegoLabelSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'form', 'helpers', 'regoLabel']);
};
